import { Link, useNavigate, useLocation } from "react-router-dom";
import { LayoutGrid, LogOut, Shield, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const navItems = [
    { to: "/dashboard", label: "I miei libri", icon: LayoutGrid },
  ];
  if (user?.role === "admin") navItems.push({ to: "/admin", label: "Team", icon: Shield });

  return (
    <div className="min-h-screen bg-ink">
      {/* Sidebar (desktop) */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-white/5 bg-[#0A1124] p-6 lg:flex">
        <Logo />
        <nav className="mt-12 flex flex-col gap-1">
          {navItems.map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label}`}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  active ? "bg-white/5 text-white" : "text-white/50 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="mt-auto rounded-lg border border-white/5 bg-[#050A15] p-4">
          <p className="flex items-center gap-2 text-xs text-white/40">
            <BookOpen className="h-3.5 w-3.5 text-[#D4AF37]" /> Hai bisogno di aiuto?
          </p>
          <p className="mt-1 text-xs text-white/30">Il team ti segue in ogni passaggio, qui nella dashboard.</p>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-white/5 bg-[#050A15]/70 px-6 py-4 backdrop-blur-xl">
          <div className="lg:hidden">
            <Logo />
          </div>
          <div className="ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="flex items-center gap-3 rounded-full py-1 pl-1 pr-3 transition-colors hover:bg-white/5">
                  <Avatar className="h-8 w-8">
                    {user?.picture && <AvatarImage src={user.picture} />}
                    <AvatarFallback className="bg-[#D4AF37]/15 text-xs text-[#D4AF37]">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden text-sm text-white/80 sm:block">{user?.name || user?.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 border-white/10 bg-[#0A1124] text-white">
                <DropdownMenuLabel className="text-white/50">
                  <p className="text-sm text-white">{user?.name}</p>
                  <p className="text-xs font-normal text-white/40">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  data-testid="logout-btn"
                  onClick={doLogout}
                  className="cursor-pointer text-white/80 focus:bg-white/5 focus:text-white"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="px-4 py-6 pb-24 sm:px-6 sm:py-10 lg:px-12 lg:pb-10">{children}</main>
      </div>

      {/* Bottom nav (mobile) */}
      <nav
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-white/5 bg-[#0A1124]/95 backdrop-blur-xl lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-nav"
      >
        {navItems.map((item) => {
          const active = location.pathname === item.to;
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.label}`}
              className={`flex flex-1 flex-col items-center gap-1 py-3 text-[11px] transition-colors ${
                active ? "text-[#D4AF37]" : "text-white/50 hover:text-white"
              }`}
            >
              <Icon className="h-5 w-5" />
              {item.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};
